import { reaction } from 'mobx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CALL_ALERT_DURATION_MS } from 'app/components/inbox/IncomingCallCard'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useLiveAlertPreferences } from 'app/hooks/useLiveAlertPreferences'
import { playChime } from 'app/lib/alertChime'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { useIncomingCallStore } from 'app/stores/StoreContext'
import type { LocationJson } from 'app/types/entities'
import type {
  IncomingCall,
  IncomingCallAlert,
  IncomingCallContact,
  MockIncomingCallOptions,
} from 'app/types/incoming-call'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { readAlertLedger, restorableAlert, writeAlertLedger } from 'lib/incomingCallAlertLedger'
import { phoneMatchSearch } from 'lib/incomingCallMatch'
import { logError } from 'lib/logging'

const CONTACT_FIELDS = `id name preferredName lifecycleStage becameLeadDate becamePatientDate`

/**
 * Dev-only, like `trkMockSmsDelivery`: set to `'1'` and `window.trkMockIncomingCall({ phone })`
 * fakes a ring. Who the card lists follows from `phone`, so mock a number a contact really holds.
 */
const MOCK_INCOMING_CALL_STORAGE_KEY = `trkMockIncomingCall`

declare global {
  interface Window {
    trkMockIncomingCall?: (opts: MockIncomingCallOptions) => void
  }
}

function mockEnabled(): boolean {
  try {
    return typeof localStorage !== `undefined` && localStorage.getItem(MOCK_INCOMING_CALL_STORAGE_KEY) === `1`
    // oxlint-disable-next-line local/require-catch-logging
  } catch {
    return false
  }
}

/** Decides which calls become cards: workspace must match the tab's own, never twice, newest first. */
export function selectIncomingCalls({
  calls,
  workspaceId,
  isSeen,
}: {
  calls: IncomingCall[]
  workspaceId: string
  isSeen: (id: string) => boolean
}): { alerts: IncomingCall[]; seenIds: string[] } {
  const fresh: IncomingCall[] = []
  const seenIds: string[] = []
  const inBatch = new Set<string>()
  for (const call of calls) {
    if (call.workspaceId !== workspaceId) continue
    if (isSeen(call.id) || inBatch.has(call.id)) continue
    inBatch.add(call.id)
    seenIds.push(call.id)
    fresh.push(call)
  }
  fresh.sort((a, b) => b.startedAt.localeCompare(a.startedAt))
  // One card, since one call gets answered at a time. The rest are still seen, so a later change
  // cannot resurface them as new.
  return { alerts: fresh.slice(0, 1), seenIds }
}

/**
 * Ringing-call alerts for `<MessageAlertStack>`, sourced from `IncomingCallStore` (which `AppLayout`
 * feeds from the event stream). The hook keeps its own copy of what it has shown, so the store's TTL
 * never pulls a card out from under someone: only its decay or the X removes it.
 */
export function useIncomingCallAlerts(): {
  alerts: IncomingCallAlert[]
  /** Everyone holding each ringing number, by call id. Absent while the lookup is out. */
  matches: Record<string, IncomingCallContact[] | undefined>
  locationNames: Record<string, string>
  dismiss: (id: string) => void
  pin: (id: string) => void
  setCallerContact: (callId: string, contact: IncomingCallContact) => void
} {
  const store = useIncomingCallStore()
  const user = useUser()
  // Literal on purpose: bin/generate-feature-toggle-names.mjs extracts names
  // from these call sites, and a constant would leave the toggle unloaded.
  const toggleOn = useFeatureToggle(`incoming-call-alerts`)
  const liveAlerts = useLiveAlertPreferences()
  const workspaceId = user?.workspaceId ?? ``
  // `loaded` keeps the source quiet until the preference is known: on is the
  // default, so an unresolved setting looks exactly like on, and a card that
  // flashes past someone who switched it off reads as the switch being broken.
  const enabled = !!(toggleOn && workspaceId && liveAlerts.loaded && liveAlerts.calls.popup)

  const [alerts, setAlerts] = useState<IncomingCallAlert[]>([])
  const [matches, setMatches] = useState<Record<string, IncomingCallContact[] | undefined>>({})
  const [locationNames, setLocationNames] = useState<Record<string, string>>({})

  const ledgerRef = useRef<IncomingCallAlert[]>(undefined)
  ledgerRef.current ??= readAlertLedger()
  const restoredRef = useRef(false)
  const audioRef = useRef<HTMLAudioElement | undefined>(undefined)
  // A ref, because the subscription closure outlives a change to the sound
  // switch: the effect is keyed on `enabled`, so muting mid-session must not
  // tear the subscription down and lose the calls it has already seen.
  const soundRef = useRef(liveAlerts.calls.sound)
  const locationNamesRef = useRef<Record<string, string>>({})

  useEffect(() => {
    soundRef.current = liveAlerts.calls.sound
  }, [liveAlerts.calls.sound])

  useEffect(() => {
    locationNamesRef.current = locationNames
  }, [locationNames])

  const updateLedger = useCallback((id: string, change: Partial<Pick<IncomingCallAlert, 'pinned' | 'dismissed'>>) => {
    ledgerRef.current = writeAlertLedger(
      ledgerRef.current!.map((alert) => (alert.call.id === id ? { ...alert, ...change } : alert)),
    )
  }, [])

  const dismiss = useCallback(
    (id: string) => {
      updateLedger(id, { dismissed: true })
      setAlerts((prev) => prev.filter((alert) => alert.call.id !== id))
    },
    [updateLedger],
  )

  const pin = useCallback(
    (id: string) => {
      updateLedger(id, { pinned: true })
      setAlerts((prev) => prev.map((alert) => (alert.call.id === id ? { ...alert, pinned: true } : alert)))
    },
    [updateLedger],
  )

  // Set from the saved record, not by re-running the phone search: a search straight after the write
  // can land before the index has the new contact.
  const setCallerContact = useCallback((callId: string, contact: IncomingCallContact) => {
    setMatches((prev) => ({ ...prev, [callId]: [contact] }))
  }, [])

  useEffect(() => {
    if (!enabled) return
    bg(async () => {
      try {
        const { hits } = await gqlFetchList<LocationJson>(`locations`, { pagination: { limit: 200 } }, `id name`, {
          requestPolicy: `network-only`,
        })
        const next: Record<string, string> = {}
        for (const hit of hits) if (hit.id && hit.name) next[hit.id] = hit.name
        setLocationNames(next)
      } catch (err) {
        logError(`Error fetching location names for incoming call alerts`, err)
      }
    })
  }, [enabled])

  // Only a real switch resets: the session refetch can briefly hand back no user during an API
  // blip, and that must not wipe a card someone is reading mid-call.
  const lastWorkspaceRef = useRef(workspaceId)
  useEffect(() => {
    if (!workspaceId || workspaceId === lastWorkspaceRef.current) return
    lastWorkspaceRef.current = workspaceId
    setAlerts([])
    setMatches({})
  }, [workspaceId])

  useEffect(() => {
    if (!enabled) return

    // `call.contactIds` is ignored on purpose; see `phoneMatchSearch`.
    const resolveMatches = async (items: IncomingCall[]) => {
      for (const call of items) {
        const search = phoneMatchSearch(call)
        if (!search) {
          setMatches((prev) => ({ ...prev, [call.id]: [] }))
          continue
        }
        try {
          // network-only: the last ring may have just created the contact this one should find.
          const { hits } = await gqlFetchList<IncomingCallContact>(
            `contacts`,
            { search, pagination: { limit: 50 } },
            CONTACT_FIELDS,
            { requestPolicy: `network-only` },
          )
          setMatches((prev) => ({ ...prev, [call.id]: hits }))
        } catch (err) {
          // Left absent, not empty: an unreachable API must not label a known patient a new number.
          logError(`Error looking up contacts for incoming call`, err)
        }
      }
    }

    const accept = (calls: IncomingCall[]) => {
      const { alerts: fresh, seenIds } = selectIncomingCalls({
        calls,
        workspaceId,
        isSeen: (id) => ledgerRef.current!.some((alert) => alert.call.id === id),
      })
      if (!fresh.length) return
      const shownAt = Date.now()
      const callsById = new Map(calls.map((call) => [call.id, call]))
      const shown: IncomingCallAlert = { call: fresh[0]!, shownAt, pinned: false, dismissed: false }
      // The row holds one call card, so whatever was up is replaced, and a later page load must not bring it back.
      ledgerRef.current = writeAlertLedger([
        ...ledgerRef.current!.map((alert) => ({ ...alert, dismissed: true })),
        ...seenIds
          .filter((id) => id !== shown.call.id)
          .map((id) => ({ call: callsById.get(id)!, shownAt, pinned: false, dismissed: true })),
        shown,
      ])
      setAlerts([shown])
      // Replaced wholesale with `alerts`, so the map cannot grow over a day at a front desk.
      setMatches({})
      if (soundRef.current)
        playChime(
          audioRef,
          fresh.map((call) => call.id),
        )
      bg(resolveMatches(fresh))
    }

    if (!restoredRef.current) {
      restoredRef.current = true
      const restored = restorableAlert({
        alerts: ledgerRef.current!,
        workspaceId,
        now: Date.now(),
        durationMs: CALL_ALERT_DURATION_MS,
      })
      if (restored) {
        setAlerts([restored])
        bg(resolveMatches([restored.call]))
      }
    }

    const stop = reaction(() => store.calls, accept, { fireImmediately: true })

    if (mockEnabled()) {
      window.trkMockIncomingCall = (opts) =>
        store.add({
          id: `mock-${Date.now()}`,
          workspaceId,
          locationId: opts.locationId ?? Object.keys(locationNamesRef.current)[0] ?? ``,
          callerPhone: opts.phone,
          callerName: opts.name,
          callerCity: opts.city ?? `Atlanta`,
          callerState: opts.state ?? `GA`,
          source: opts.source ?? `Google Ads`,
          startedAt: new Date().toISOString(),
          // Unread by the alert, which looks contacts up by phone instead.
          contactIds: [],
        })
    }

    return () => {
      stop()
      delete window.trkMockIncomingCall
    }
  }, [enabled, workspaceId, store])

  return { alerts, matches, locationNames, dismiss, pin, setCallerContact }
}
